import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  ApiError,
  ImportScope,
  ProbeRaid,
  ProbeResponse,
  RecentReport,
  api,
  clearToken,
  getToken,
  isAbortError,
  setCurrentUser,
  setToken,
} from "../api";
import Spinner from "../components/Spinner";
import { useReport } from "../context/ReportContext";
import type { EnrichPayload, RosterEntry } from "../lib/combatLogParser";
import type { WorkerMessage } from "../lib/combatLogParser.worker";
import { rosterFromReport } from "../lib/roster";

export default function Admin() {
  const { setSlug } = useReport();
  const [token, setLocalToken] = useState<string | null>(getToken());

  // Drop any previously-loaded report from the context the moment the user
  // lands on /admin. Sidebar gates its rendering on URL match anyway, but
  // clearing the context here makes the invariant explicit and avoids any
  // brief flash of stale data on slow re-renders.
  useEffect(() => {
    setSlug(null);
  }, [setSlug]);

  // Stable identity so ImportPanel's `useEffect([onLogout])` doesn't refire
  // every render — without useCallback, the inline arrow is a new function
  // reference each time, which made the recent-imports endpoint hit on every
  // re-render of this parent.
  const handleLogout = useCallback(() => {
    clearToken();
    setLocalToken(null);
  }, []);

  if (!token) return <Login onSuccess={(t) => setLocalToken(t)} />;
  return <ImportPanel onLogout={handleLogout} />;
}

function Login({ onSuccess }: { onSuccess: (token: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(username.trim(), password);
      setToken(res.token);
      setCurrentUser({
        username: res.username,
        role: res.role,
        displayName: res.displayName,
      });
      onSuccess(res.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="max-w-sm space-y-3">
      <h1 className="text-xl font-semibold">Admin login</h1>
      <input
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="Username"
        autoComplete="username"
        className="w-full rounded border border-border bg-panel px-3 py-2 outline-none focus:border-accent"
        autoFocus
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        autoComplete="current-password"
        className="w-full rounded border border-border bg-panel px-3 py-2 outline-none focus:border-accent"
      />
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={busy || !username || !password}
        className="rounded bg-accent px-4 py-2 font-medium text-black disabled:opacity-50"
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

function ImportPanel({ onLogout }: { onLogout: () => void }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [recent, setRecent] = useState<RecentReport[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Drives the in-progress status panel under the form. The button
  // alone reading "Analyzing…" wasn't enough feedback — a 5-15s wait
  // with no motion looks frozen, especially on a fresh page load
  // where the user is still scanning the layout.
  const [phase, setPhase] = useState<ImportPhase>({ kind: "idle" });
  const elapsed = useElapsedSeconds(
    phase.kind === "idle" ? null : phase.startedAt,
  );
  // Single-raid dedupe hit — show "Open existing" / "Re-analyze" prompt.
  // `raid` is captured so the post-re-analyze results modal can show it,
  // and `scope` so re-analyze can target the same Firestore doc.
  const [conflict, setConflict] = useState<{
    slug: string;
    code: string;
    scope?: ImportScope;
    raid?: ProbeRaid;
  } | null>(null);
  // Multi-raid log detected — show the raid-selection modal.
  const [probe, setProbe] = useState<ProbeResponse | null>(null);
  // Per-raid status while the multi-raid import runs.
  const [progress, setProgress] = useState<RaidImportStatus[] | null>(null);
  // Final per-raid outcome — drives the post-import "share & open" modal.
  // Populated when an import finishes (single or multi raid). Cleared
  // when the user closes the modal.
  const [results, setResults] = useState<RaidImportStatus[] | null>(null);
  // Report currently being enriched with a combat log (opens EnrichModal).
  const [enrichFor, setEnrichFor] = useState<RecentReport | null>(null);

  useEffect(() => {
    // AbortController cancels the in-flight fetch when this effect tears
    // down. Important in dev because React 18 StrictMode runs effects
    // mount → cleanup → mount on first render — without aborting, both
    // requests would hit the network even though only the second one's
    // result is used.
    const ctrl = new AbortController();
    setRecentLoading(true);
    api
      .recent(ctrl.signal, showArchived)
      .then((items) => setRecent(items))
      .catch((e) => {
        if (isAbortError(e)) return;
        if (e instanceof ApiError && e.status === 401) onLogout();
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setRecentLoading(false);
      });
    return () => ctrl.abort();
  }, [onLogout, showArchived]);

  async function setStatus(slug: string, status: "active" | "archived") {
    // Optimistic update — flip the row immediately, roll back on error.
    setRecent((prev) =>
      prev.map((r) => (r.slug === slug ? { ...r, status } : r)),
    );
    try {
      await api.setReportStatus(slug, status);
      // If the user is on "active only" and just archived a row, drop it
      // from the list so it disappears without a refetch.
      if (!showArchived && status === "archived") {
        setRecent((prev) => prev.filter((r) => r.slug !== slug));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Status update failed");
      // Revert.
      setRecent((prev) =>
        prev.map((r) =>
          r.slug === slug
            ? { ...r, status: status === "archived" ? "active" : "archived" }
            : r,
        ),
      );
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setPhase({ kind: "probing", startedAt: Date.now() });
    try {
      // Probe first — cheap WCL META_QUERY only. The backend groups
      // fights by gameZone so we know if this is a multi-raid log.
      const probed = await api.probeImport(code);
      if (probed.raids.length === 0) {
        setError("WCL log has no fights to analyze.");
        return;
      }
      if (probed.raids.length === 1) {
        // Single raid — import directly with that scope. Skip the modal.
        const r = probed.raids[0];
        const scope = scopeFor(r);
        setPhase({
          kind: "importing",
          startedAt: Date.now(),
          raidName: r.zoneName,
        });
        const res = await api.importReport(probed.code, false, scope);
        if (res.existed) {
          setConflict({ slug: res.slug, code: res.wclCode, scope, raid: r });
        } else {
          setResults([{ raid: r, state: "done", slug: res.slug }]);
          api
            .recent(undefined, showArchived)
            .then(setRecent)
            .catch(() => {});
        }
        return;
      }
      // Multi-raid — let the user pick which raids to analyze. The
      // picker modal has its own per-raid progress UI, so we drop the
      // top-level phase here.
      setProbe(probed);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Probe failed");
    } finally {
      setBusy(false);
      setPhase({ kind: "idle" });
    }
  }

  async function importSelected(picks: ProbeRaid[], force: boolean) {
    if (!probe || picks.length === 0) return;
    setBusy(true);
    setError(null);
    setProgress(picks.map((r) => ({ raid: r, state: "pending" })));
    const finals: RaidImportStatus[] = [];
    for (let i = 0; i < picks.length; i++) {
      const r = picks[i];
      setProgress(
        (prev) =>
          prev?.map((p, j) => (j === i ? { ...p, state: "running" } : p)) ??
          null,
      );
      try {
        const res = await api.importReport(probe.code, force, scopeFor(r));
        const status: RaidImportStatus = {
          raid: r,
          state: res.existed ? "existed" : "done",
          slug: res.slug,
        };
        finals.push(status);
        setProgress(
          (prev) => prev?.map((p, j) => (j === i ? status : p)) ?? null,
        );
      } catch (err) {
        const status: RaidImportStatus = {
          raid: r,
          state: "error",
          message: err instanceof Error ? err.message : "Import failed",
        };
        finals.push(status);
        setProgress(
          (prev) => prev?.map((p, j) => (j === i ? status : p)) ?? null,
        );
      }
    }
    setBusy(false);
    // Refresh the recent-imports list so the new rows show up.
    api
      .recent(undefined, showArchived)
      .then(setRecent)
      .catch(() => {});
    // Hand off to the results modal. We give the picker a brief moment
    // to show the final "Imported" badge before swapping so the user
    // sees the loop completed. Errors stay visible until the user
    // closes the picker manually — they'd want to read the message
    // before the modal swap eats it.
    const anyErrors = finals.some((s) => s.state === "error");
    if (!anyErrors) {
      setTimeout(() => {
        setProbe(null);
        setProgress(null);
        setResults(finals);
      }, 500);
    }
  }

  function closeRaidModal() {
    if (busy) return;
    setProbe(null);
    setProgress(null);
  }

  async function reanalyze() {
    if (!conflict) return;
    setBusy(true);
    setError(null);
    setPhase({
      kind: "importing",
      startedAt: Date.now(),
      raidName: conflict.raid?.zoneName,
    });
    try {
      // Reuse the scope that produced the conflict so the overwrite
      // targets the same Firestore doc — otherwise a scoped report
      // would be re-analyzed as a legacy whole-report and a duplicate
      // slug could appear.
      const res = await api.importReport(conflict.code, true, conflict.scope);
      const raid = conflict.raid;
      setConflict(null);
      api
        .recent(undefined, showArchived)
        .then(setRecent)
        .catch(() => {});
      if (raid) {
        setResults([{ raid, state: "done", slug: res.slug }]);
      } else {
        navigate(`/r/${res.slug}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Re-analyze failed");
    } finally {
      setBusy(false);
      setPhase({ kind: "idle" });
    }
  }

  function openExisting() {
    if (!conflict) return;
    const slug = conflict.slug;
    setConflict(null);
    navigate(`/r/${slug}`);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Import a report</h1>
        <button
          onClick={onLogout}
          className="text-sm text-gray-400 hover:text-gray-200"
        >
          Sign out
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3 max-w-2xl">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Paste a Warcraft Logs report code or full URL"
          className="w-full rounded border border-border bg-panel px-3 py-2 outline-none focus:border-accent"
          autoFocus
        />
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={busy || !code}
          className="inline-flex items-center gap-2 rounded bg-accent px-4 py-2 font-medium text-black disabled:opacity-50"
        >
          {busy && <ButtonSpinner />}
          <span>{busy ? "Analyzing…" : "Analyze"}</span>
        </button>
        {phase.kind !== "idle" && !probe && (
          <ImportPhasePanel phase={phase} elapsed={elapsed} />
        )}
      </form>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent imports</h2>
          <label className="flex items-center gap-2 text-xs text-gray-400 select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-accent"
            />
            Show archived
          </label>
        </div>
        {recentLoading ? (
          <Spinner label="Loading recent imports…" />
        ) : recent.length === 0 ? (
          <p className="text-gray-400 text-sm">
            {showArchived ? "Nothing here." : "None yet."}
          </p>
        ) : (
          <ul className="divide-y divide-border/60 rounded border border-border/60 bg-bg/40">
            {recent.map((r) => {
              const archived = r.status === "archived";
              return (
                <li
                  key={r.slug}
                  className="flex items-start justify-between gap-3 px-3 py-2.5"
                >
                  <Link
                    to={`/r/${r.slug}`}
                    className="min-w-0 flex-1 group"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          "truncate text-sm font-semibold group-hover:underline " +
                          (archived ? "text-gray-500" : "text-accent")
                        }
                      >
                        {r.zone || "Unknown zone"}
                      </span>
                      {archived && (
                        <span className="text-[10px] uppercase tracking-wider text-gray-600 flex-shrink-0">
                          archived
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                      <span className="text-gray-400 truncate max-w-[18rem]">
                        {r.reportTitle || r.slug}
                      </span>
                      <span className="text-gray-700">·</span>
                      <span title="Date the raid happened">
                        Raid:{" "}
                        <span className="text-gray-300 tabular-nums">
                          {fmtRecentDate(r.raidStartedAt) ?? "-"}
                        </span>
                      </span>
                      <span className="text-gray-700">·</span>
                      <span title="Date this analysis was imported">
                        Imported:{" "}
                        <span className="text-gray-300 tabular-nums">
                          {fmtRecentDate(r.createdAt) ?? "-"}
                        </span>
                      </span>
                    </div>
                  </Link>
                  <div className="flex flex-shrink-0 items-center gap-1 self-center">
                    <button
                      onClick={() => setEnrichFor(r)}
                      className={
                        "rounded px-2 py-1 text-[11px] hover:bg-white/5 " +
                        (r.hasEnrichment
                          ? "text-emerald-400 hover:text-emerald-300"
                          : "text-gray-500 hover:text-gray-200")
                      }
                      title={
                        r.hasEnrichment
                          ? "Combat log attached — re-enrich or remove"
                          : "Enrich with a combat log"
                      }
                    >
                      {r.hasEnrichment ? "Enriched ✓" : "Enrich"}
                    </button>
                    <button
                      onClick={() =>
                        setStatus(r.slug, archived ? "active" : "archived")
                      }
                      className="rounded px-2 py-1 text-[11px] text-gray-500 hover:bg-white/5 hover:text-gray-200"
                      title={archived ? "Unarchive" : "Archive"}
                    >
                      {archived ? "Unarchive" : "Archive"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {conflict && (
        <ConflictDialog
          code={conflict.code}
          busy={busy}
          onOpen={openExisting}
          onReanalyze={reanalyze}
          onCancel={() => setConflict(null)}
        />
      )}

      {probe && (
        <RaidPickerModal
          probe={probe}
          progress={progress}
          busy={busy}
          onSubmit={importSelected}
          onClose={closeRaidModal}
        />
      )}

      {results && (
        <ImportResultsModal
          results={results}
          onClose={() => setResults(null)}
        />
      )}

      {enrichFor && (
        <EnrichModal
          report={enrichFor}
          onClose={() => setEnrichFor(null)}
          onChanged={() => {
            api
              .recent(undefined, showArchived)
              .then(setRecent)
              .catch(() => {});
          }}
        />
      )}
    </div>
  );
}

interface RaidImportStatus {
  raid: ProbeRaid;
  state: "pending" | "running" | "done" | "existed" | "error";
  slug?: string;
  message?: string;
}

type ImportPhase =
  | { kind: "idle" }
  | { kind: "probing"; startedAt: number }
  | { kind: "importing"; startedAt: number; raidName?: string };

/**
 * Tick once a second while `startedAt` is non-null. Returns whole
 * seconds since that epoch ms, or 0 when idle. The form uses this to
 * fade in an "Elapsed: Ns" hint after a few seconds — quick imports
 * don't flash the counter.
 */
function useElapsedSeconds(startedAt: number | null): number {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (startedAt == null) {
      setElapsed(0);
      return;
    }
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);
  return elapsed;
}

function ButtonSpinner() {
  return (
    <span
      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/30 border-t-black"
      aria-hidden
    />
  );
}

function ImportPhasePanel({
  phase,
  elapsed,
}: {
  phase: ImportPhase;
  elapsed: number;
}) {
  if (phase.kind === "idle") return null;
  const headline =
    phase.kind === "probing"
      ? "Detecting raids in the log…"
      : phase.raidName
        ? `Analyzing ${phase.raidName}…`
        : "Analyzing report…";
  const subline =
    phase.kind === "probing"
      ? "Asking Warcraft Logs which fights are in this report."
      : "Pulling combat data and running metric analysis. Usually 5-15 seconds.";
  return (
    <div className="rounded border border-border/60 bg-bg/40 px-3 py-2 flex items-center gap-3">
      <Spinner size={14} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-200 truncate">{headline}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{subline}</div>
      </div>
      {elapsed >= 3 && (
        <div className="flex-shrink-0 text-[11px] tabular-nums text-gray-500">
          {elapsed}s
        </div>
      )}
    </div>
  );
}

function fmtRecentDate(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function scopeFor(r: ProbeRaid): ImportScope {
  return {
    zoneId: r.zoneId,
    zoneName: r.zoneName,
    fightIds: r.fightIds,
  };
}

function fmtTime(unixMs: number): string {
  const d = new Date(unixMs);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function RaidPickerModal({
  probe,
  progress,
  busy,
  onSubmit,
  onClose,
}: {
  probe: ProbeResponse;
  progress: RaidImportStatus[] | null;
  busy: boolean;
  onSubmit: (picks: ProbeRaid[], force: boolean) => void;
  onClose: () => void;
}) {
  // Default-pick: all raids that aren't already imported. If everything
  // is already imported we still default-tick everything so a force
  // re-analyze is one click away.
  const [picked, setPicked] = useState<Set<number>>(() => {
    const out = new Set<number>();
    const allImported = probe.raids.every((r) => r.existingSlug);
    for (const r of probe.raids) {
      if (allImported || !r.existingSlug) out.add(r.zoneId);
    }
    return out;
  });
  const [force, setForce] = useState(false);

  const togglePick = (zid: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(zid)) next.delete(zid);
      else next.add(zid);
      return next;
    });
  };

  const picks = probe.raids.filter((r) => picked.has(r.zoneId));
  const anyExisting = picks.some((r) => r.existingSlug);
  const submitDisabled = busy || picks.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-border bg-panel shadow-xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-border px-5 py-3">
          <h3 className="text-base font-bold">Multiple raids detected</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            This log spans {probe.raids.length} raid zones. Pick which raid(s)
            to analyze, each one becomes its own report.
          </p>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <ul className="space-y-1.5">
            {probe.raids.map((r) => {
              const checked = picked.has(r.zoneId);
              const status = progress?.find((p) => p.raid.zoneId === r.zoneId);
              return (
                <li
                  key={r.zoneId}
                  className={[
                    "rounded border px-3 py-2 cursor-pointer transition-colors",
                    checked
                      ? "border-accent/40 bg-accent/5"
                      : "border-border/60 bg-bg/40 hover:bg-white/5",
                    busy ? "pointer-events-none opacity-80" : "",
                  ].join(" ")}
                  onClick={() => !busy && togglePick(r.zoneId)}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePick(r.zoneId)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={busy}
                      className="accent-accent flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold text-gray-100 truncate">
                          {r.zoneName}
                        </span>
                        <span className="text-[11px] tabular-nums text-gray-500 flex-shrink-0">
                          {fmtTime(r.startTime)} – {fmtTime(r.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>
                          <span className="text-gray-300 tabular-nums">
                            {r.bossFightCount}
                          </span>{" "}
                          boss{r.bossFightCount === 1 ? "" : "es"}
                        </span>
                        <span className="tabular-nums">
                          <span className="text-emerald-300">{r.kills}</span>
                          <span className="text-gray-700 mx-0.5">/</span>
                          <span className="text-red-300">{r.wipes}</span>
                          <span className="text-gray-500 ml-1">
                            ({r.kills + r.wipes} pulls)
                          </span>
                        </span>
                        {r.existingSlug && !status && (
                          <span className="text-[10px] uppercase tracking-wider text-amber-400">
                            already imported
                          </span>
                        )}
                      </div>
                    </div>
                    <RaidStatusBadge status={status} />
                  </div>
                  {status?.state === "error" && status.message && (
                    <p className="mt-1.5 text-[11px] text-red-300/90 break-words pl-7">
                      {status.message}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <footer className="border-t border-border px-5 py-3 flex items-center justify-between gap-3">
          <label
            className={
              "flex items-center gap-2 text-xs select-none " +
              (anyExisting ? "text-gray-300" : "text-gray-600")
            }
            title={
              anyExisting
                ? "Re-fetch and overwrite already-imported raids"
                : "No selected raids are already imported"
            }
          >
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              disabled={busy || !anyExisting}
              className="accent-accent"
            />
            Force re-analyze existing
          </label>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 disabled:opacity-50"
            >
              {progress ? "Close" : "Cancel"}
            </button>
            <button
              onClick={() => onSubmit(picks, force)}
              disabled={submitDisabled}
              className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-black disabled:opacity-50"
            >
              {busy
                ? "Analyzing…"
                : `Import ${picks.length} raid${picks.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ImportResultsModal({
  results,
  onClose,
}: {
  results: RaidImportStatus[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const newCount = results.filter((r) => r.state === "done").length;
  const existedCount = results.filter((r) => r.state === "existed").length;
  const errorCount = results.filter((r) => r.state === "error").length;

  // Close on Escape, lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-lg border border-border bg-panel shadow-xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-border px-5 py-3">
          <h3 className="text-base font-bold">Import complete</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {summarizeCounts(newCount, existedCount, errorCount)}
            {results.length > 0 && newCount + existedCount > 0
              ? ". Share the link with the raid or open a report below."
              : ""}
          </p>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {results.map((r, i) => (
            <ImportResultRow
              key={`${r.raid.zoneId}-${i}`}
              result={r}
              onOpen={(slug) => {
                onClose();
                navigate(`/r/${slug}`);
              }}
            />
          ))}
        </div>

        <footer className="border-t border-border px-5 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-gray-300 hover:bg-white/5"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function summarizeCounts(
  newCount: number,
  existedCount: number,
  errorCount: number,
): string {
  const parts: string[] = [];
  if (newCount) parts.push(`${newCount} new`);
  if (existedCount) parts.push(`${existedCount} reused`);
  if (errorCount) parts.push(`${errorCount} failed`);
  return parts.length ? parts.join(" · ") : "Nothing to do";
}

function ImportResultRow({
  result,
  onOpen,
}: {
  result: RaidImportStatus;
  onOpen: (slug: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = result.slug ? `${window.location.origin}/r/${result.slug}` : null;

  const onCopy = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      // Reset the "Copied!" indicator after a beat so the user can copy
      // again without it being permanently stuck on success.
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can refuse (insecure context, focus loss, etc).
      // Fall back to a manual select prompt.
      window.prompt("Copy this URL:", url);
    }
  }, [url]);

  if (result.state === "error") {
    return (
      <div className="rounded border border-red-500/30 bg-red-500/5 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-100">
            {result.raid.zoneName}
          </span>
          <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-red-500/15 text-red-300">
            Error
          </span>
        </div>
        <p className="text-xs text-red-300/80 mt-1">
          {result.message ?? "Import failed."}
        </p>
      </div>
    );
  }

  if (!url || !result.slug) return null;

  return (
    <div className="rounded border border-border/60 bg-bg/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="font-semibold text-gray-100 truncate">
          {result.raid.zoneName}
        </span>
        <RaidStatusBadge status={result} />
      </div>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 min-w-0 truncate rounded bg-bg/60 px-2 py-1 text-[11px] text-gray-300"
          title={url}
        >
          {url}
        </code>
        <button
          onClick={onCopy}
          className={
            "flex-shrink-0 rounded border border-border/60 px-2 py-1 text-[11px] font-medium transition-colors " +
            (copied
              ? "bg-emerald-500/15 text-emerald-300"
              : "text-gray-300 hover:bg-white/5")
          }
        >
          {copied ? "Copied!" : "Copy"}
        </button>
        <button
          onClick={() => result.slug && onOpen(result.slug)}
          className="flex-shrink-0 rounded bg-accent px-2 py-1 text-[11px] font-medium text-black hover:bg-accent/90"
        >
          Open →
        </button>
      </div>
    </div>
  );
}

function RaidStatusBadge({ status }: { status?: RaidImportStatus }) {
  if (!status) return null;
  const base =
    "text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded flex-shrink-0";
  switch (status.state) {
    case "pending":
      return (
        <span className={base + " bg-gray-500/15 text-gray-400"}>Queued</span>
      );
    case "running":
      return (
        <span
          className={base + " bg-accent/15 text-accent inline-flex items-center gap-1"}
        >
          <span
            className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-accent/30 border-t-accent"
            aria-hidden
          />
          Running…
        </span>
      );
    case "done":
      return (
        <span className={base + " bg-emerald-500/15 text-emerald-300"}>
          Imported
        </span>
      );
    case "existed":
      return (
        <span
          className={base + " bg-amber-500/15 text-amber-300"}
          title="Reused existing slug"
        >
          Existed
        </span>
      );
    case "error":
      return (
        <span
          className={base + " bg-red-500/15 text-red-300"}
          title={status.message}
        >
          Error
        </span>
      );
  }
}

/* ----------------------- Combat-log enrichment ----------------------- */

type EnrichPhase =
  | { kind: "loading" } // fetching the report for scope + roster
  | { kind: "ready" } // waiting for a file pick
  | { kind: "parsing"; pct: number }
  | { kind: "parsed"; payload: EnrichPayload }
  | { kind: "uploading" }
  | { kind: "done"; combats: number; casts: number }
  | { kind: "error"; message: string };

function payloadStats(p: EnrichPayload): {
  combats: number;
  casts: number;
  players: number;
  bosses: number;
} {
  const players = new Set<string>();
  let casts = 0;
  let bosses = 0;
  for (const c of p.combats) {
    if (c.encounterId) bosses++;
    for (const [name, list] of Object.entries(c.casts)) {
      players.add(name);
      casts += list.length;
    }
  }
  return { combats: p.combats.length, casts, players: players.size, bosses };
}

function EnrichModal({
  report,
  onClose,
  onChanged,
}: {
  report: RecentReport;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [phase, setPhase] = useState<EnrichPhase>({ kind: "loading" });
  const [scopeZoneId, setScopeZoneId] = useState<number | null>(null);
  const [hasEnrichment, setHasEnrichment] = useState(!!report.hasEnrichment);
  const rosterRef = useRef<Record<string, RosterEntry>>({});
  const workerRef = useRef<Worker | null>(null);

  // Pull the report once for its scope zone + player→class roster.
  useEffect(() => {
    let cancelled = false;
    api
      .getReport(report.slug)
      .then((doc) => {
        if (cancelled) return;
        rosterRef.current = rosterFromReport(doc);
        setScopeZoneId(doc.scopeZoneId ?? doc.zoneId ?? null);
        setPhase({ kind: "ready" });
      })
      .catch((e) => {
        if (!cancelled)
          setPhase({
            kind: "error",
            message: e instanceof Error ? e.message : "Failed to load report",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [report.slug]);

  // Kill the worker if the modal unmounts mid-parse.
  useEffect(() => () => workerRef.current?.terminate(), []);

  const busy = phase.kind === "parsing" || phase.kind === "uploading";

  function onFile(file: File) {
    const worker = new Worker(
      new URL("../lib/combatLogParser.worker.ts", import.meta.url),
      { type: "module" },
    );
    workerRef.current = worker;
    setPhase({ kind: "parsing", pct: 0 });
    worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
      const msg = e.data;
      if (msg.type === "progress") {
        const pct = msg.totalBytes
          ? Math.round((msg.bytes / msg.totalBytes) * 100)
          : 0;
        setPhase({ kind: "parsing", pct });
      } else if (msg.type === "done") {
        worker.terminate();
        workerRef.current = null;
        setPhase({ kind: "parsed", payload: msg.payload });
      } else if (msg.type === "error") {
        worker.terminate();
        workerRef.current = null;
        setPhase({ kind: "error", message: msg.message });
      }
    };
    worker.onerror = (ev) => {
      worker.terminate();
      workerRef.current = null;
      setPhase({ kind: "error", message: ev.message || "Worker failed" });
    };
    worker.postMessage({
      file,
      scopeZoneId,
      roster: rosterRef.current,
    });
  }

  async function upload(payload: EnrichPayload) {
    setPhase({ kind: "uploading" });
    try {
      const res = await api.enrichReport(report.slug, payload);
      setHasEnrichment(true);
      onChanged();
      setPhase({ kind: "done", combats: res.combatCount, casts: res.castCount });
    } catch (e) {
      setPhase({
        kind: "error",
        message: e instanceof Error ? e.message : "Upload failed",
      });
    }
  }

  async function remove() {
    setPhase({ kind: "uploading" });
    try {
      await api.deleteEnrichment(report.slug);
      setHasEnrichment(false);
      onChanged();
      onClose();
    } catch (e) {
      setPhase({
        kind: "error",
        message: e instanceof Error ? e.message : "Delete failed",
      });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-border bg-panel shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-border px-5 py-3">
          <h3 className="text-base font-bold">
            Enrich with combat log
            {hasEnrichment && (
              <span className="ml-2 align-middle text-[10px] uppercase tracking-wider text-emerald-400">
                attached
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {report.zone || "Report"} · the log is parsed in your browser; only
            the distilled per-combat GCD data is uploaded.
          </p>
        </header>

        <div className="px-5 py-4 space-y-3 text-sm">
          {phase.kind === "loading" && <Spinner label="Loading report…" />}

          {phase.kind === "error" && (
            <p className="text-red-400 text-sm">{phase.message}</p>
          )}

          {(phase.kind === "ready" || phase.kind === "error") && (
            <>
              {scopeZoneId == null && (
                <p className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300/90">
                  This report isn't scoped to a single zone, so every combat in
                  the log will be included.
                </p>
              )}
              <FilePicker onPick={onFile} />
              <p className="text-xs text-gray-500">
                Pick the <code>WoWCombatLog-*.txt</code> for this raid night.
                Combats outside this report's zone are dropped automatically.
              </p>
            </>
          )}

          {phase.kind === "parsing" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>Parsing combat log…</span>
                <span className="tabular-nums">{phase.pct}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded bg-bg/60">
                <div
                  className="h-full bg-accent transition-[width] duration-150"
                  style={{ width: `${phase.pct}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Large logs can take a minute. The file never leaves your machine.
              </p>
            </div>
          )}

          {phase.kind === "parsed" && (
            <ParsedSummary
              payload={phase.payload}
              hasEnrichment={hasEnrichment}
              onUpload={() => upload(phase.payload)}
              onReset={() => setPhase({ kind: "ready" })}
            />
          )}

          {phase.kind === "uploading" && <Spinner label="Uploading…" />}

          {phase.kind === "done" && (
            <p className="rounded border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-300">
              Done — {phase.combats} combats, {phase.casts.toLocaleString()}{" "}
              casts. The Combat Log section is now on this report.
            </p>
          )}
        </div>

        <footer className="border-t border-border px-5 py-3 flex items-center justify-between gap-3">
          <div>
            {hasEnrichment && !busy && (
              <button
                onClick={remove}
                className="rounded px-2 py-1.5 text-xs text-red-300 hover:bg-red-500/10"
              >
                Delete enrichment
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded px-3 py-1.5 text-sm text-gray-300 hover:bg-white/5 disabled:opacity-50"
          >
            {phase.kind === "done" ? "Close" : "Cancel"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function FilePicker({ onPick }: { onPick: (file: File) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-center rounded border border-dashed border-border bg-bg/40 px-4 py-6 text-sm text-gray-400 hover:border-accent hover:text-gray-200">
      <input
        type="file"
        accept=".txt,text/plain"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          // Reset so picking the same file again re-triggers onChange.
          e.target.value = "";
        }}
      />
      <span>Click to choose a combat log (.txt)</span>
    </label>
  );
}

function ParsedSummary({
  payload,
  hasEnrichment,
  onUpload,
  onReset,
}: {
  payload: EnrichPayload;
  hasEnrichment: boolean;
  onUpload: () => void;
  onReset: () => void;
}) {
  const s = payloadStats(payload);
  if (s.combats === 0) {
    return (
      <div className="space-y-3">
        <p className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300/90">
          No combats matched this report's zone. Make sure you picked the log
          from the right raid night.
        </p>
        <button
          onClick={onReset}
          className="rounded border border-border bg-bg/40 px-3 py-1.5 text-sm hover:bg-white/5"
        >
          Choose another file
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <Stat label="Combats" value={s.combats} />
        <Stat label="Boss pulls" value={s.bosses} />
        <Stat label="Players" value={s.players} />
        <Stat label="Casts" value={s.casts.toLocaleString()} />
      </dl>
      <div className="flex items-center gap-2">
        <button
          onClick={onUpload}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-black hover:bg-accent/90"
        >
          {hasEnrichment ? "Overwrite enrichment" : "Upload enrichment"}
        </button>
        <button
          onClick={onReset}
          className="rounded border border-border bg-bg/40 px-3 py-1.5 text-sm hover:bg-white/5"
        >
          Choose another file
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded border border-border/60 bg-bg/40 px-3 py-2">
      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="tabular-nums font-semibold text-gray-100">{value}</dd>
    </div>
  );
}

function ConflictDialog({
  code,
  busy,
  onOpen,
  onReanalyze,
  onCancel,
}: {
  code: string;
  busy: boolean;
  onOpen: () => void;
  onReanalyze: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-panel p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold mb-1">Already analyzed</h3>
        <p className="text-sm text-gray-400 mb-4">
          A report for <code className="text-gray-300">{code}</code> already
          exists. You can open the existing analysis, or overwrite it with a
          fresh re-analyze (the previous data will be replaced).
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onOpen}
            disabled={busy}
            className="rounded border border-border bg-bg/40 px-3 py-1.5 text-sm hover:bg-white/5 disabled:opacity-50"
          >
            Open existing
          </button>
          <button
            onClick={onReanalyze}
            disabled={busy}
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-black disabled:opacity-50"
          >
            {busy ? "Re-analyzing…" : "Re-analyze"}
          </button>
        </div>
      </div>
    </div>
  );
}
